/**
 * Every sound name the game can ask for, in one place: the manifest
 * (client/public/audio/manifest.json) maps these to files, the Sfx asks for
 * them, and tests/client/audio.test.ts holds the two sides together. A name
 * with no file falls back to the synth recipe in sfxPatches.ts, so a missing
 * file is a quieter game, never a silent one.
 *
 * Naming: `group.thing`, lower case, dots between levels, dashes inside a
 * level. Variants of one sound are separate files under the one name.
 */

export const SOUND_ELEMENTS = ['water', 'life', 'shield', 'cold', 'lightning', 'arcane', 'earth', 'fire'] as const;
export const SOUND_FUSIONS = ['steam', 'ice', 'mud', 'lava', 'spring'] as const;
/** Woven pieces (docs/spells-depth.md): each has a queue chime and a cast voice layered on its form's cast sound. */
export const SOUND_WOVEN = [
  'plasma', 'flux', 'stillness', 'obsidian', 'ether', 'cloud', 'ash', 'rime', 'moment', 'dragonfire', 'stormglass', 'thunderhead', 'time',
  'tide', 'pulse', 'void', 'wildfire', 'whirlpool', 'deluge', 'inferno', 'glacier', 'soul', 'sun', 'winter', 'death',
] as const;
/**
 * Deeper spells: a weave starting and finishing (one sting per tier), one that
 * won't hold or breaks; a piece going into the stash and coming out; a first
 * discovery; a Grand Magick's incantation; Time Stop stopping, its slow loop and
 * time starting again.
 */
export const SOUND_DEPTH = [
  'weave.start', 'weave.two', 'weave.three', 'weave.four', 'weave.five', 'weave.fail', 'weave.break', 'stash.in', 'stash.out', 'discover',
  'grand.incant', 'grand.timestop', 'loop.timestop', 'timestop.end',
  // slice 2: a tier 5 weave finishing, each Grand Magick's moment, a ward turning a killing blow, a Death mark falling
  'grand.quickening', 'grand.rewind', 'grand.greatflood', 'grand.supernova', 'grand.eternalwinter', 'grand.doom', 'ward', 'doom',
] as const;
export const SOUND_CAST_KINDS = ['beam', 'spray', 'arc', 'projectile', 'projectile.earth', 'nova', 'quake', 'barrier', 'wall', 'mines', 'storm', 'aura', 'armor', 'self', 'melee', 'wind', 'field', 'blink'] as const;
export const SOUND_DAMAGE = ['physical', 'fire', 'cold', 'lightning', 'arcane', 'steam', 'water', 'life'] as const;
export const SOUND_STATUSES = ['wet', 'burning', 'chilled', 'frozen', 'shocked', 'knockdown', 'hasted', 'slowed', 'mired', 'blinded'] as const;
export const SOUND_BOOMS = ['blast', 'beamcross', 'bomb', 'mine', 'meteor', 'vortex', 'slam', 'frost'] as const;
export const SOUND_MAGICKS = [
  'revive', 'haste', 'teleport', 'thunderbolt', 'rain', 'nullify', 'timewarp', 'conflagration', 'phoenix', 'blizzard',
  'meteor', 'tornado', 'thunderstorm', 'vortex', 'elemental', 'stoptime',
] as const;
export const SOUND_ENEMIES = ['spider', 'rogue', 'ranger', 'bomber', 'shaman', 'troll', 'treespirit', 'skeleton', 'orc', 'jotunn', 'mire'] as const;
export const SOUND_STINGS = ['wave', 'breather', 'gameover', 'lobby', 'victory'] as const;
/**
 * `boss` is the jotunn's first phase, `boss-rage` its second. `cloister-explore`
 * and `cloister-fight` are story mode's two act 1 layers: one loop length, played
 * in step and crossfaded while a room is fighting (GameAudio.layers).
 */
export const SOUND_MUSIC = ['lobby', 'wave-low', 'wave-high', 'boss', 'boss-rage', 'gameover', 'results', 'cloister-explore', 'cloister-fight', 'mire', 'ctf', 'victory', 'defeat'] as const;
/**
 * Story mode (slice 2): doors slamming shut, opening, the rune door; a
 * brazier catching and going out; a rune stone claimed; the Book's page
 * turn under each caption; spiders out of the floor cracks; an ice bridge
 * forming, cracking in its last seconds and melting.
 */
export const SOUND_STORY = [
  'door.slam', 'door.open', 'door.rune', 'brazier.catch', 'brazier.douse', 'checkpoint', 'ui.caption', 'spawn.crack',
  'ice.form', 'ice.crack', 'ice.melt',
  // act 1 rooms 3-5: the boiler, the portcullis, brambles, the chasm
  'boiler.fill', 'boiler.gauge', 'loop.boiler', 'portcullis', 'bramble.burn', 'lava.fill', 'lava.set', 'fall',
  // the Sunken Chapel: Mother Mire's flood and pool, the Everflame
  'mire.flood', 'mire.sink', 'mire.rise', 'everflame',
] as const;
/** Capture the flag (slice 5): a flag taken, dropped, sent home, captured. */
export const SOUND_PVP = ['flag.take', 'flag.drop', 'flag.return', 'flag.capture'] as const;
/** The elements a storm wall's sustain comes in (the others borrow the nearest: lava fire, mud water...). */
export const SOUND_STORM_ELEMENTS = ['fire', 'cold', 'water', 'lightning', 'steam', 'arcane', 'life'] as const;
/** The elements with a nova of their own (the rest play `cast.nova`). */
export const SOUND_NOVA_ELEMENTS = ['fire', 'cold', 'water', 'lightning', 'arcane', 'life'] as const;
/** The elements a spray's sustain comes in (the others borrow the nearest: ice cold, earth mud, life spring). */
export const SOUND_SPRAY_ELEMENTS = ['water', 'fire', 'cold', 'steam', 'mud', 'lava', 'spring'] as const;
/**
 * The spells overhaul: a shield breaking or wearing out, rock and ice walls breaking, an ice wall rising, a
 * bubble popping, a mine arming, a frozen body thawing, each element's nova and each storm wall's sustain; then
 * (pass B) each spray's sustain, chain lightning's crackle, two beams clashing as they cross and bounce, and the
 * ice shards thrown.
 */
export const SOUND_SPELLS: readonly string[] = [
  'shield.break', 'shield.fade', 'wall.crumble', 'wall.shatter', 'cast.wall.ice', 'bubble.pop', 'mine.arm', 'status.thaw',
  ...SOUND_NOVA_ELEMENTS.map((e) => `cast.nova.${e}`),
  ...SOUND_STORM_ELEMENTS.map((e) => `loop.storm.${e}`),
  ...SOUND_SPRAY_ELEMENTS.map((e) => `loop.spray.${e}`),
  'loop.arc', 'beam.clash', 'cast.projectile.ice',
];

/** The full list, built from the groups above plus the one-offs. */
export const SOUND_NAMES: readonly string[] = [
  ...SOUND_ELEMENTS.map((e) => `queue.${e}`),
  ...SOUND_FUSIONS.map((f) => `queue.${f}`),
  ...SOUND_WOVEN.map((w) => `queue.${w}`),
  ...SOUND_WOVEN.map((w) => `voice.${w}`),
  ...SOUND_DEPTH,
  'queue.degrade', 'queue.cancel', 'queue.full', 'queue.fizzle', 'shock.self',
  ...SOUND_CAST_KINDS.map((k) => `cast.${k}`),
  ...[...SOUND_ELEMENTS, ...SOUND_FUSIONS].map((e) => `loop.beam.${e}`),
  'loop.beam.remote', 'loop.burn', 'loop.charge',
  ...SOUND_DAMAGE.map((d) => `hit.${d}`),
  'hit.crit', 'heal',
  ...SOUND_STATUSES.map((s) => `status.${s}`),
  ...SOUND_BOOMS.map((b) => `boom.${b}`),
  'beam.merge',
  ...SOUND_MAGICKS.map((m) => `magick.${m}`),
  'imbue', 'thunder', 'death.player', 'death.enemy', 'revive', 'oops',
  ...SOUND_STINGS.map((s) => `sting.${s}`),
  'tick',
  ...SOUND_ENEMIES.map((e) => `tell.${e}`),
  // The boss's tell wind-up and its phase-2 roar; a tornado's howl while one is on the field.
  'boss.tell', 'boss.rage', 'loop.tornado',
  'spawn', 'step',
  'ui.click', 'ui.open', 'ui.close', 'ui.ready', 'ui.chat',
  ...SOUND_MUSIC.map((m) => `music.${m}`),
  'amb.arena',
  ...SOUND_STORY,
  ...SOUND_PVP,
  ...SOUND_SPELLS,
];

const NAME_SET = new Set(SOUND_NAMES);

export function isSoundName(name: string): boolean {
  return NAME_SET.has(name);
}
